import { PAGE_SIZE_MAX, ReduceRequestSchema, ReducedFrameSchema } from '../views/contract'
import type { ReduceRequest, ReducedFrame } from '../views/contract'
import { fetchV1, readV1 } from './http'
import { PlenumApiError } from './envelope'

export class ReduceError extends Error {
  readonly status: number
  readonly plenumError?: PlenumApiError
  constructor(status: number, message: string, plenumError?: PlenumApiError) {
    super(message)
    this.name = 'ReduceError'
    this.status = status
    this.plenumError = plenumError
  }
}

/** The most rows one answer is read to on the client (the server's own
 *  `limit` ceiling). Past it the frame keeps a `page_cap` drop naming what is
 *  left — never a silent cut. */
export const REDUCE_MAX_ROWS = 100_000

type Frame = ReturnType<typeof ReducedFrameSchema.parse>

/** One question through the read coalescer (api/batch.ts via http.readV1):
 *  questions asked together — a record page's widgets, a dashboard's tiles —
 *  travel as ONE POST /v1/…/batch, each answered exactly as its own request
 *  would be. With no bearer identity (a dev member-id header) it goes alone. */
async function postQuestion(slug: string, payload: string): Promise<unknown> {
  return readV1<unknown>(`/v1/tenants/${slug}/queries`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload })
}

async function postReduce(slug: string, body: unknown): Promise<Frame> {
  try {
    const data = await postQuestion(slug, JSON.stringify(body))
    return ReducedFrameSchema.parse(data)
  } catch (e) {
    if (e instanceof PlenumApiError) throw new ReduceError(e.status, e.message, e)
    throw e
  }
}

/** POST /v1/tenants/{slug}/queries — the COMPLETE answer.
 *
 *  The server pages a long answer (200 rows by default) and names the next
 *  page in `meta.page.cursor`. Every frame renderer (map, chart, stat,
 *  pivot, …) draws all of an answer's rows, so this follows the cursor to the
 *  end in one place: the first request is sent exactly as asked (an answer
 *  of one page is returned untouched), each next page is read at the
 *  server's largest page size, and the merged frame carries no page meta.
 *  A caller that sets `page` itself pages on its own: one page, as served.
 *  Past REDUCE_MAX_ROWS the frame keeps a `page_cap` drop for the rest. */
export async function reduce(slug: string, request: ReduceRequest): Promise<ReducedFrame> {
  const validRequest = ReduceRequestSchema.parse(request)
  const first = await postReduce(slug, validRequest)
  if (validRequest.page != null || !first.meta.page?.cursor) return first as ReducedFrame

  const rows = [...first.rows]
  const totalRows = first.meta.page.total_rows
  let cursor: string | null | undefined = first.meta.page.cursor
  while (cursor && rows.length < REDUCE_MAX_ROWS) {
    const size = Math.min(PAGE_SIZE_MAX, REDUCE_MAX_ROWS - rows.length)
    const next = await postReduce(slug, { ...validRequest, page: { size, cursor } })
    rows.push(...next.rows)
    cursor = next.meta.page?.cursor
  }
  const { page: _page, ...meta } = first.meta
  const dropped = meta.dropped.filter((d) => d.reason !== 'page_cap')
  const left = Math.max(totalRows - rows.length, 0)
  if (cursor && left > 0) {
    dropped.push({
      reason: 'page_cap', count: left,
      detail: `this view reads at most ${REDUCE_MAX_ROWS.toLocaleString('en-US')} rows of one answer`,
    })
  }
  return { rows, meta: { ...meta, dropped } } as ReducedFrame
}

export interface QuantityInfo {
  slug: string
  display_name: string
  kind: 'raw' | 'ratio'
  applies_to: string
  unit?: string | null
  unit_composition?: unknown
}

/** GET /v1/tenants/{slug}/quantities — the canonical quantity catalog in the
 *  Plenum envelope. `data.quantities` entries are the parity model
 *  (monad/plenum/v1/data.py QuantityEntry), a superset of QuantityInfo. */
export async function getQuantities(slug: string, entityDef: string): Promise<QuantityInfo[]> {
  try {
    const data = await fetchV1<{ quantities: QuantityInfo[] }>(
      `/v1/tenants/${slug}/quantities?entity_def=${encodeURIComponent(entityDef)}`,
    )
    return data.quantities
  } catch (e) {
    if (e instanceof PlenumApiError) throw new ReduceError(e.status, e.message, e)
    throw e
  }
}
